import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
    LogOut, Menu, X,
    ChefHat, Calendar, Users, Wine, Sparkles, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

// Import modular components
import {
    MenuItemsSection,
    DailyMenuSection,
    ReservationsSection,
    DiningEventsSection,
    ServiceRequestsSection,
    MenuItemModal,
    DailyMenuModal,
    DiningEventModal,
    ReservationModal,
    ServiceRequestModal,
    DeleteConfirmModal
} from '../components/manager-dining';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ManagerDiningServicesPage() {
    const navigate = useNavigate();
    const { token, logout } = useAuth();
    const [activeSection, setActiveSection] = useState('menu');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Data states
    const [menuItems, setMenuItems] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [diningEvents, setDiningEvents] = useState([]);
    const [serviceRequests, setServiceRequests] = useState([]);
    
    // Modal states
    const [menuItemModal, setMenuItemModal] = useState({ open: false, data: null });
    const [dailyMenuModal, setDailyMenuModal] = useState({ open: false, data: null });
    const [diningEventModal, setDiningEventModal] = useState({ open: false, data: null });
    const [reservationModal, setReservationModal] = useState({ open: false, data: null });
    const [serviceRequestModal, setServiceRequestModal] = useState({ open: false, data: null });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: null, name: '' });
    const [newNote, setNewNote] = useState('');

    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [menuRes, reservationsRes, diningEventsRes, serviceRequestsRes] = await Promise.all([
                axios.get(`${API}/dining/menu-items`, { headers }),
                axios.get(`${API}/manager/dining/reservations`, { headers }),
                axios.get(`${API}/dining/events`, { headers }),
                axios.get(`${API}/manager/services/requests`, { headers })
            ]);
            
            setMenuItems(menuRes.data);
            setReservations(reservationsRes.data);
            setDiningEvents(diningEventsRes.data);
            setServiceRequests(serviceRequestsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Menu Item Handlers
    const handleSaveMenuItem = async () => {
        const data = menuItemModal.data;
        if (!data.name || !data.category) {
            toast.error('Please fill in name and category');
            return;
        }

        const isNew = !data.id;
        const endpoint = isNew ? `${API}/manager/dining/menu-items` : `${API}/manager/dining/menu-items/${data.id}`;
        
        try {
            await axios[isNew ? 'post' : 'put'](endpoint, data, { headers });
            toast.success(`Menu item ${isNew ? 'created' : 'updated'}`);
            setMenuItemModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to save menu item');
        }
    };

    const handleDeleteMenuItem = async (id) => {
        try {
            await axios.delete(`${API}/manager/dining/menu-items/${id}`, { headers });
            toast.success('Menu item deleted');
            setDeleteConfirm({ open: false, type: '', id: null, name: '' });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to delete menu item');
        }
    };

    const openNewMenuItemModal = () => {
        setMenuItemModal({
            open: true,
            data: {
                name: '', description: '', price: '', category: 'main',
                is_vegetarian: false, is_vegan: false, is_gluten_free: false, is_available: true
            }
        });
    };

    // Daily Menu Handlers
    const handleSaveDailyMenu = async () => {
        const data = dailyMenuModal.data;
        if (!data.date) {
            toast.error('Please select a date');
            return;
        }

        try {
            await axios.post(`${API}/manager/dining/daily-menu`, data, { headers });
            toast.success('Daily menu saved');
            setDailyMenuModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to save daily menu');
        }
    };

    const openDailyMenuModal = () => {
        setDailyMenuModal({
            open: true,
            data: { date: new Date().toISOString().split('T')[0], menu_item_ids: [], chef_special: '' }
        });
    };

    // Dining Event Handlers
    const handleSaveDiningEvent = async () => {
        const data = diningEventModal.data;
        if (!data.title || !data.date) {
            toast.error('Please fill in title and date');
            return;
        }

        try {
            await axios.post(`${API}/manager/dining/events`, data, { headers });
            toast.success('Dining event created');
            setDiningEventModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to create dining event');
        }
    };

    const openNewDiningEventModal = () => {
        setDiningEventModal({
            open: true,
            data: { title: '', description: '', date: new Date().toISOString().split('T')[0], time: '19:00', price: '', capacity: 20, current_bookings: 0 }
        });
    };

    // Reservation Handlers
    const handleUpdateReservation = async () => {
        const data = reservationModal.data;
        try {
            await axios.put(`${API}/manager/dining/reservations/${data.id}`, {
                status: data.status, table_number: data.table_number, notes: data.notes
            }, { headers });
            toast.success('Reservation updated');
            setReservationModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to update reservation');
        }
    };

    // Service Request Handlers
    const handleUpdateServiceRequest = async () => {
        const data = serviceRequestModal.data;
        const payload = { status: data.status };
        if (newNote.trim()) payload.note = newNote.trim();
        
        try {
            await axios.put(`${API}/manager/services/requests/${data.id}`, payload, { headers });
            toast.success('Service request updated');
            setServiceRequestModal({ open: false, data: null });
            setNewNote('');
            fetchAllData();
        } catch (error) {
            toast.error('Failed to update service request');
        }
    };

    // Delete confirmation handler
    const handleDeleteConfirm = () => {
        if (deleteConfirm.type === 'menu') handleDeleteMenuItem(deleteConfirm.id);
    };

    // Navigation
    const navItems = [
        { id: 'menu', icon: ChefHat, label: 'Menu Items' },
        { id: 'daily', icon: Calendar, label: 'Daily Menu' },
        { id: 'reservations', icon: Users, label: 'Reservations' },
        { id: 'dining-events', icon: Wine, label: 'Dining Events' },
        { id: 'services', icon: Sparkles, label: 'Service Requests' },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-background flex flex-col md:flex-row" data-testid="manager-dining-services-page">
            {/* Mobile Header */}
            <div className="md:hidden sticky top-0 left-0 right-0 bg-rose-700 z-40 p-4 flex items-center justify-between">
                <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white p-2" data-testid="mobile-menu-btn">
                    {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
                <h1 className="text-white font-bold">Dining & Services CMS</h1>
                <button onClick={handleLogout} className="text-white p-2">
                    <LogOut className="w-5 h-5" />
                </button>
            </div>

            {/* Mobile Navigation Dropdown */}
            {sidebarOpen && (
                <div className="md:hidden bg-rose-700 border-b border-white/10 p-4 z-30">
                    <nav className="space-y-1">
                        <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-2 px-3">Dining</p>
                        {navItems.slice(0, 4).map(item => (
                            <button
                                key={item.id}
                                onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                                    activeSection === item.id ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                                }`}
                            >
                                <item.icon className="w-5 h-5" />
                                <span>{item.label}</span>
                            </button>
                        ))}
                        <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mt-4 mb-2 px-3">Services</p>
                        {navItems.slice(4).map(item => (
                            <button
                                key={item.id}
                                onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                                    activeSection === item.id ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                                }`}
                            >
                                <item.icon className="w-5 h-5" />
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            {/* Desktop Sidebar */}
            <aside className="hidden md:flex md:flex-col md:w-64 md:min-h-screen bg-rose-700 flex-shrink-0">
                <div className="p-4 border-b border-white/10">
                    <h1 className="text-xl font-bold text-white">Dining & Services</h1>
                    <p className="text-white/60 text-sm">Manager CMS</p>
                </div>
                
                <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
                    <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-2 px-3">Dining</p>
                    {navItems.slice(0, 4).map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                                activeSection === item.id ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                            }`}
                            data-testid={`nav-${item.id}`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span>{item.label}</span>
                        </button>
                    ))}
                    
                    <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mt-6 mb-2 px-3">Services</p>
                    {navItems.slice(4).map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                                activeSection === item.id ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                            }`}
                            data-testid={`nav-${item.id}`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span>{item.label}</span>
                        </button>
                    ))}
                </nav>

                <div className="p-4 border-t border-white/10 space-y-2">
                    <button 
                        onClick={() => navigate('/manager')} 
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                    >
                        <MessageSquare className="w-5 h-5" />
                        <span>Main Manager Panel</span>
                    </button>
                    <button 
                        onClick={handleLogout} 
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Logout</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-4 md:p-8 overflow-auto">
                <div className="max-w-6xl mx-auto">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="w-8 h-8 border-4 border-rose-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <>
                            {activeSection === 'menu' && (
                                <MenuItemsSection 
                                    menuItems={menuItems}
                                    onAddItem={openNewMenuItemModal}
                                    onEditItem={(item) => setMenuItemModal({ open: true, data: item })}
                                    onDeleteItem={(item) => setDeleteConfirm({ open: true, type: 'menu', id: item.id, name: item.name })}
                                />
                            )}

                            {activeSection === 'daily' && (
                                <DailyMenuSection 
                                    menuItems={menuItems}
                                    onSetDailyMenu={openDailyMenuModal}
                                />
                            )}

                            {activeSection === 'reservations' && (
                                <ReservationsSection 
                                    reservations={reservations}
                                    onManageReservation={(res) => setReservationModal({ open: true, data: res })}
                                />
                            )}

                            {activeSection === 'dining-events' && (
                                <DiningEventsSection 
                                    diningEvents={diningEvents}
                                    onAddEvent={openNewDiningEventModal}
                                />
                            )}

                            {activeSection === 'services' && (
                                <ServiceRequestsSection 
                                    serviceRequests={serviceRequests}
                                    onManageRequest={(req) => setServiceRequestModal({ open: true, data: req })}
                                />
                            )}
                        </>
                    )}
                </div>
            </main>

            {/* Modals */}
            <MenuItemModal modal={menuItemModal} setModal={setMenuItemModal} onSave={handleSaveMenuItem} />
            <DailyMenuModal modal={dailyMenuModal} setModal={setDailyMenuModal} menuItems={menuItems} onSave={handleSaveDailyMenu} />
            <DiningEventModal modal={diningEventModal} setModal={setDiningEventModal} onSave={handleSaveDiningEvent} />
            <ReservationModal modal={reservationModal} setModal={setReservationModal} onSave={handleUpdateReservation} />
            <ServiceRequestModal modal={serviceRequestModal} setModal={setServiceRequestModal} newNote={newNote} setNewNote={setNewNote} onSave={handleUpdateServiceRequest} />
            <DeleteConfirmModal deleteConfirm={deleteConfirm} setDeleteConfirm={setDeleteConfirm} onConfirm={handleDeleteConfirm} />
        </div>
    );
}

export default ManagerDiningServicesPage;
